import type { PublicDepositAccount } from './depositAccountService'

export type PendingDepositMethod = 'bank_transfer' | 'prompt_pay' | 'true_money' | 'decimal_reservation'

export interface PendingDepositSenderAccount {
  bankCode?: string
  bankName?: string
  accountNumber?: string
  accountName?: string
}

export interface PendingDepositDraft {
  id: string
  tenantId: string
  userId: string
  amount: string
  depositMethod: PendingDepositMethod
  depositAccount?: PublicDepositAccount
  senderAccount?: PendingDepositSenderAccount
  usesPaymentGateway?: boolean
  paymentTransactionId?: string
  paymentExternalRefId?: string
  createdAt: string
  updatedAt: string
}

const STORAGE_KEY_PREFIX = 'pending-deposit-drafts:v1'
const MAX_DRAFT_AGE_MS = 7 * 24 * 60 * 60 * 1000

const getStorageKey = (tenantId: string, userId: string) => `${STORAGE_KEY_PREFIX}:${tenantId}:${userId}`

const readDrafts = (tenantId: string, userId: string): PendingDepositDraft[] => {
  try {
    const raw = localStorage.getItem(getStorageKey(tenantId, userId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as PendingDepositDraft[]
    if (!Array.isArray(parsed)) return []
    const now = Date.now()
    return parsed.filter((draft) => {
      const createdAt = Date.parse(draft.createdAt || '')
      return Number.isFinite(createdAt) && now - createdAt <= MAX_DRAFT_AGE_MS
    })
  } catch {
    return []
  }
}

const writeDrafts = (tenantId: string, userId: string, drafts: PendingDepositDraft[]) => {
  try {
    localStorage.setItem(getStorageKey(tenantId, userId), JSON.stringify(drafts))
  } catch {
    // Ignore local storage failures and keep the flow usable without draft persistence.
  }
}

export const pendingDepositDraftService = {
  list(tenantId: string, userId: string): PendingDepositDraft[] {
    return readDrafts(tenantId, userId).sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
  },

  getById(tenantId: string, userId: string, draftId: string): PendingDepositDraft | null {
    return this.list(tenantId, userId).find((draft) => draft.id === draftId) || null
  },

  upsert(
    tenantId: string,
    userId: string,
    draft: Omit<PendingDepositDraft, 'tenantId' | 'userId' | 'createdAt' | 'updatedAt'> & {
      createdAt?: string
      updatedAt?: string
    }
  ): PendingDepositDraft {
    const drafts = readDrafts(tenantId, userId)
    const now = new Date().toISOString()
    const existing = drafts.find((item) => item.id === draft.id)

    const nextDraft: PendingDepositDraft = {
      id: draft.id,
      tenantId,
      userId,
      amount: draft.amount,
      depositMethod: draft.depositMethod,
      depositAccount: draft.depositAccount,
      senderAccount: draft.senderAccount,
      usesPaymentGateway: draft.usesPaymentGateway === true,
      paymentTransactionId: draft.paymentTransactionId,
      paymentExternalRefId: draft.paymentExternalRefId,
      createdAt: existing?.createdAt || draft.createdAt || now,
      updatedAt: draft.updatedAt || now,
    }

    const nextDrafts = existing
      ? drafts.map((item) => (item.id === draft.id ? nextDraft : item))
      : [nextDraft, ...drafts]

    writeDrafts(tenantId, userId, nextDrafts)
    return nextDraft
  },

  remove(tenantId: string, userId: string, draftId: string) {
    const drafts = readDrafts(tenantId, userId)
    writeDrafts(
      tenantId,
      userId,
      drafts.filter((draft) => draft.id !== draftId)
    )
  },
}
